/**
 * Cliente fetch tipado para la API de Tijera.
 * - Adjunta Authorization: Bearer <token> automaticamente si hay token.
 * - Captura X-Refreshed-Token y notifica para que AuthContext lo guarde.
 * - Devuelve { ok, data?, error? } siempre — los callers no manejan throws.
 */

const RAW = (import.meta as ImportMeta & { env: { VITE_API_BASE?: string } }).env.VITE_API_BASE;
export const API_BASE = RAW || 'https://tijera-api-staging.macasta2000.workers.dev';

export type ApiResult<T> =
  | { ok: true; data: T; refreshedToken?: string }
  | { ok: false; error: string; status: number };

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  token?: string | null;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.token) headers['Authorization'] = `Bearer ${opts.token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    return { ok: false, error: 'network_error', status: 0 };
  }

  const refreshedToken = res.headers.get('X-Refreshed-Token') ?? undefined;

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    return { ok: false, error: 'invalid_response', status: res.status };
  }

  const body = json as { ok?: boolean; error?: string };
  if (!res.ok || body.ok === false) {
    return { ok: false, error: body.error ?? `http_${res.status}`, status: res.status };
  }

  return { ok: true, data: json as T, refreshedToken };
}
